import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import 'react-dates/initialize';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import moment from 'moment-timezone';
import { emptyArray } from 'ui-components/default-props/empty';
import { Button } from '../button';
import { DatePickerDropdown } from '../date-picker-dropdown';
import { DropdownMenuPositionPropType } from '../../prop-types/dropdown-menu-position';

import { FloatLabel } from '../float-label';
import style from './float-date-picker.module.css';

const getFirstValidDateFormat = (formats, date) => {
  const validFormat = formats.find((format) => moment(date, format, true).isValid());
  if (validFormat) {
    return moment(date, validFormat, true);
  }
};

const formatDateValue = (value, dateFormat, additionalDateFormats, sellerTimeZone) => {
  const allDateFormats = [dateFormat, ...additionalDateFormats];
  const parsedSelectedDate = getFirstValidDateFormat(allDateFormats, value) || moment(value);
  if (parsedSelectedDate.isValid()) {
    const inputValue = sellerTimeZone ? parsedSelectedDate.tz(sellerTimeZone).format(dateFormat) : parsedSelectedDate.format(dateFormat);
    return inputValue;
  }
  return '';
};

// class FloatDatePicker extends React.Component {
export const FloatDatePicker = ({
  label,
  value,
  onChange,
  dateFormat = 'MM/DD/YYYY',
  sellerTimeZone = null,
  allowPastDates = false,
  calendarPosition = 'bottom-left',
  className = '',
  isOutsideRange = null,
  hasError = false,
  additionalDateFormats = emptyArray,
}) => {
  const [slideLabelUp, setSlideLabelUp] = useState(!!value);
  const [inputValue, setInputValue] = useState(formatDateValue(value, dateFormat, additionalDateFormats, sellerTimeZone));

  const onFocus = () => setSlideLabelUp(true);

  const onInputChange = (e) => {
    setInputValue(e.target.value);
  };

  /*
    TODO: The onChange return value "contract" is messy and leads to inconsistent upstream consumers of various date pickers
      There are 4 possible example forms of date value that get passed on props.onChange:
      "07/16/2020", null, moment Object, "2020-07-16T05:00:00.000Z" (ISO string) compared to date-picker
      that just returns a formatted date string or "" (1st example)
  */
  const onDateChange = (newValue) => {
    if (newValue === null) {
      setInputValue('');
      setSlideLabelUp(false);
      return onChange(value);
    }

    if (typeof newValue === 'object') {
      const formattedDate = newValue.format(dateFormat);
      setInputValue(formattedDate);
      setSlideLabelUp(!!formattedDate.length);
      return onChange(newValue.toISOString());
    }

    setInputValue(newValue);
    setSlideLabelUp(!!newValue.length);
    onChange(moment(newValue, dateFormat).toISOString());
  };

  const classes = classnames(style['float-date-picker'], className);

  const onInputBlur = (e) => {
    if (!e.target.value.length) {
      setSlideLabelUp(false);
      onDateChange(null);
      return;
    }

    const allDateFormats = [dateFormat, ...additionalDateFormats];
    const parsedDate = getFirstValidDateFormat(allDateFormats, e.target.value);
    if (!parsedDate) {
      return;
    }

    if (parsedDate.isBefore(moment()) && !allowPastDates) {
      const formattedPropValue = formatDateValue(value, dateFormat, additionalDateFormats);
      setSlideLabelUp(!!formattedPropValue.length);
      setInputValue(formattedPropValue);
      return;
    }

    onDateChange(parsedDate);
    setSlideLabelUp(true);
  };

  return (
    <div className={classes}>
      <div
        className={classnames(style['float-date-picker-content'], {
          [style.error]: hasError,
        })}
      >
        <div className={style['label-and-input']}>
          <FloatLabel className={style['float-label']} hasError={hasError} labelText={label} slideUp={slideLabelUp} />
          <input
            aria-label='Date Picker'
            className={classnames(style['date-input'], {
              [style['has-value']]: !!inputValue,
            })}
            onBlur={onInputBlur}
            onChange={onInputChange}
            onFocus={onFocus}
            onKeyDown={(e) => {
              const allowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '/', '.', '-', 'Tab', 'Enter', 'Delete', 'Backspace'];
              if (!allowed.includes(e.key) && !e.altKey && !e.ctrlKey && !e.metaKey && !e.key.startsWith('Arrow')) {
                e.preventDefault();
              }
            }}
            value={inputValue}
          />
        </div>
        <div className={style['date-picker-wrapper']}>
          <DatePickerDropdown
            allowPastDates={allowPastDates}
            calendarPosition={calendarPosition}
            dateFormat={dateFormat}
            isOutsideRange={isOutsideRange}
            onChange={onDateChange}
            value={value}
            verticalSpacing={-15}
          >
            <Button className={style['date-picker-button']} theme='tertiary'>
              <FontAwesomeIcon icon={faCalendar} />
            </Button>
          </DatePickerDropdown>
        </div>
      </div>
    </div>
  );
};

FloatDatePicker.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.string.isRequired, // this should be a parsable string
  onChange: PropTypes.func.isRequired,
  dateFormat: PropTypes.string,
  sellerTimeZone: PropTypes.string,
  allowPastDates: PropTypes.bool,
  calendarPosition: DropdownMenuPositionPropType,
  className: PropTypes.string,
  isOutsideRange: PropTypes.func,
  hasError: PropTypes.bool,
  additionalDateFormats: PropTypes.arrayOf(PropTypes.string),
};
